/** D:\directiveai-react\frontend\src\components\AGTChat\services\AGTChatService.js */

import axios from 'axios';
import ChatFilesService from './ChatFilesService';

/**
 * URL base para AGT Chat Backend (chat, mensajes, conversaciones)
 * Puerto 5001 en desarrollo
 */
const AGT_CHAT_API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_CHAT_URL || 'https://ore7ej9pa3.execute-api.us-east-2.amazonaws.com/prod')
  : 'http://localhost:5001';

const AGT_CHAT_STREAM_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_CHAT_STREAM_URL || AGT_CHAT_API_URL)
  : 'http://localhost:5001';

/**
 * Asumimos un timeout estándar de 5 minutos (300000 ms)
 */
const STANDARD_TIMEOUT = 5000000; // 5 minutos en milisegundos

/**
 * Función para crear interceptores de error comunes
 */
const createErrorInterceptor = (instance) => {
  instance.interceptors.response.use(
    response => response,
    error => {
      if (error.response) {
        if (error.response.status === 429) {
          error.is429 = true;
        }
      }
      return Promise.reject(error);
    }
  );
};

/**
 * Instancia de axios para AGT Chat Backend (chat, mensajes, conversaciones)
 * Puerto 5001 en desarrollo
 */
const apiChat = axios.create({
  baseURL: AGT_CHAT_API_URL,
  timeout: STANDARD_TIMEOUT,
  withCredentials: true
});

// Aplicar interceptores
createErrorInterceptor(apiChat);

/**
 * Función auxiliar para convertir Blob a Base64.
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    if (!(blob instanceof Blob)) {
      console.error('blobToBase64 recibió un valor que no es Blob:', blob);
      reject(new Error('Se esperaba un objeto Blob'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result;
        if (typeof result === 'string' && result.includes('base64,')) {
          resolve(result.split(',')[1]);
        } else {
          console.error('Formato inesperado en la conversión de blob:', result?.substring(0, 50));
          reject(new Error('Formato inválido en la conversión'));
        }
      } catch (e) {
        console.error('Error al procesar resultado de FileReader:', e);
        reject(e);
      }
    };
    reader.onerror = (e) => {
      console.error('Error en FileReader:', e);
      reject(e);
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Verifica si el navegador soporta Server-Sent Events
 * @returns {boolean}
 */
export const supportsSSE = () => {
  return 'EventSource' in window;
};

/* =========================================================================
   GESTIÓN DE CHATS Y MENSAJES
   ========================================================================= */

/**
 * Obtiene la lista de chats del usuario.
 */
export const getChats = async (userId, options = {}) => {
  const {
    limit = 20,
    lastEvaluatedKey = null,
    projectId = null,
    searchTerm = null,
    category = null
  } = options;

  try {
    const params = new URLSearchParams({
      limit: limit.toString()
    });

    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', JSON.stringify(lastEvaluatedKey));
    }

    if (projectId) {
      params.append('projectId', projectId);
    }

    if (searchTerm) {
      params.append('search', searchTerm.trim());
    }

    if (category) {
      params.append('category', category);
    }

    const response = await apiChat.get(`/api/chats/${userId}?${params}`);

    const chats = Array.isArray(response.data.chats)
      ? response.data.chats
          .filter(chat => chat && chat.id)
          .map(chat => ({
            ...chat,
            title: chat.title || chat.name || "New chat",
            updatedAt: chat.updatedAt || chat.createdAt,
            category: chat.category || 'personal',
            projectId: chat.projectId || null
          }))
      : [];

    return {
      chats,
      hasMore: response.data.hasMore,
      lastEvaluatedKey: response.data.lastEvaluatedKey,
      total: response.data.total
    };
  } catch (error) {
    console.error('Error in getChats:', error);
    return {
      chats: [],
      hasMore: false,
      lastEvaluatedKey: null,
      total: 0
    };
  }
};

/**
 * Obtiene los mensajes de un chat.
 */
export const getChatMessages = async (userId, chatId) => {
  try {
    if (!chatId) {
      return [];
    }
    const response = await apiChat.get(`/api/chat-messages/${userId}/${chatId}`);
    const messages = response.data.messages || [];
    return messages;
  } catch (error) {
    return [];
  }
};

/**
 * Crea un nuevo chat con categoría dada (por defecto "personal").
 */
export const createNewChat = async (userId, category = 'personal', projectId = null) => {
  try {
    const payload = {
      userId,
      uid: userId,
      category,
      projectId,
      type: projectId ? 'project' : 'regular'
    };

    const response = await apiChat.post('/api/create-chat', payload);

    let chatData = null;

    if (response.data && response.data.id) {
      chatData = response.data;
    } else if (response.data && response.data.success && response.data.chat) {
      chatData = response.data.chat;
    } else if (response.data && response.data.chat && response.data.chat.id) {
      chatData = response.data.chat;
    }

    if (!chatData || !chatData.id) {
      throw new Error('Failed to create chat: Invalid chat data received');
    }

    return {
      id: chatData.id,
      title: chatData.title || chatData.name || "New chat",
      name: chatData.name || chatData.title || "New chat",
      createdAt: chatData.createdAt,
      updatedAt: chatData.updatedAt || chatData.createdAt,
      category: chatData.category || category,
      projectId: chatData.projectId || projectId,
      type: chatData.type || (projectId ? 'project' : 'regular')
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Actualiza la categoría de un chat.
 */
export const updateChatCategory = async (userId, chatId, newCategory) => {
  try {
    const response = await apiChat.put('/api/update-chat-category', {
      userId,
      chatId,
      newCategory
    });
    return response.data;
  } catch (error) {
    console.error('Error en AGTChatService:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

/**
 * Obtiene el contexto de un chat (historial reducido o summary).
 */
export const getChatContext = async (userId, chatId) => {
  try {
    if (!chatId) {
      return null;
    }
    const response = await apiChat.get(`/api/chat-context/${userId}/${chatId}`);
    return response.data.context;
  } catch (error) {
    return null;
  }
};

/**
 * Actualiza el timestamp de un chat a la hora actual.
 */
export const updateChatTimestamp = async (userId, chatId) => {
  try {
    const response = await apiChat.put(`/api/update-chat-timestamp/${userId}/${chatId}?uid=${userId}`);
    return response.data.updatedAt;
  } catch (error) {
    throw new Error('Failed to update chat timestamp');
  }
};

/**
 * Elimina un chat específico.
 */
export const deleteChat = async (userId, chatId) => {
  try {
    const response = await apiChat.delete(`/api/delete-chat/${userId}/${chatId}?uid=${userId}`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to delete chat');
  }
};

/**
 * Actualiza el título de un chat
 */
export const updateChatTitle = async (userId, chatId, newTitle) => {
  try {
    const payload = {
      userId,
      uid: userId,
      chatId,
      newTitle
    };

    const response = await apiChat.put('/api/update-chat-title', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to update chat title');
  }
};

/* =========================================================================
   ENVÍO DE MENSAJES
   ========================================================================= */

/**
 * Envía un mensaje al backend con streaming SSE (Server-Sent Events)
 */
export const sendMessageToAGTStream = async (
  message,
  userId,
  chatId,
  context,
  selectedModel,
  personality = 'normal',
  userName = '',
  userEmail = '',
  selectedVoice = 'alloy',
  onToken = null,
  onDone = null,
  onError = null,
  abortSignal = null,
  onPreferencesUpdated = null // Nuevo callback para actualizaciones de preferencias
) => {
  try {
    let processedMessage = message;

    // Procesar archivos usando el nuevo sistema
    if (typeof message === 'object' && message.files && message.files.length > 0) {
      try {
        // Procesar todos los archivos con el nuevo servicio
        const result = await ChatFilesService.processChatFiles(userId, message.files);

        const processedFiles = result.files.map((processedFile, index) => {
          const originalFile = message.files[index];
          return {
            s3Key: processedFile.s3Key,
            s3Url: processedFile.s3Url,
            bucket: processedFile.bucket,
            originalName: processedFile.fileName,
            pdfText: processedFile.extractedText || '',
            type: originalFile.type,
            data: originalFile.data,
            name: originalFile.name,
            source: originalFile.source || message.source,
            captureType: message.captureType
          };
        });

        // Si hubo errores, agregar archivos que fallaron sin procesamiento
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach((error, errorIndex) => {
            const failedFileIndex = result.processed + errorIndex;
            const originalFile = message.files[failedFileIndex];
            processedFiles.push({
              type: originalFile.type,
              data: originalFile.data,
              name: originalFile.name,
              source: originalFile.source || message.source,
              captureType: message.captureType,
              processingError: error.error
            });
          });
        }

        processedMessage = {
          text: message.text || '',
          files: processedFiles.map(file => ({
            type: file.type,
            data: file.data,
            name: file.name,
            source: file.source || message.source,
            captureType: message.captureType
          })),
          storage: processedFiles.map(file => ({
            s3Key: file.s3Key,
            s3Url: file.s3Url,
            bucket: file.bucket,
            originalName: file.originalName,
            type: file.type
          })),
          pdfs: processedFiles
            .filter(f =>
              f.type === 'application/pdf' ||
              f.type === 'application/msword' ||
              f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
              f.type === 'application/vnd.ms-excel' ||
              f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
              f.type === 'text/csv' ||
              f.type === 'text/plain'
            )
            .map(f => ({
              pdfText: f.pdfText,
              pdfUrl: f.pdfUrl,
              name: f.name,
              originalName: f.originalName
            })),
          voicePreference: selectedVoice,
          source: message.source,
          captureType: message.captureType,
          metadata: {
            source: message.source,
            captureType: message.captureType,
            deviceInfo: 'web',
            timestamp: new Date().toISOString()
          }
        };
      } catch (fileProcessingError) {
        console.error('[AGTChat] Error processing files:', fileProcessingError);
        // Si falla el procesamiento, enviar archivos sin procesar
        processedMessage = {
          text: message.text || '',
          files: message.files,
          voicePreference: selectedVoice,
          source: message.source,
          captureType: message.captureType,
          metadata: {
            source: message.source,
            captureType: message.captureType,
            deviceInfo: 'web',
            timestamp: new Date().toISOString()
          }
        };
      }
    }
    else if (typeof message === 'object') {
      if (message.type === 'audio_message' ||
          (message.text && message.text.type === 'audio_message') ||
          message.audioBase64 ||
          (message.text && message.text.audioBase64)) {

        const audioData = message.type === 'audio_message'
          ? message
          : message.text && message.text.type === 'audio_message'
            ? message.text
            : message;

        let audioS3Url = message.audioS3Url ||
                        (message.text && message.text.audioS3Url) ||
                        audioData.audioS3Url;

        let audioBase64 = audioData.audioBase64;

        if (!audioBase64 && audioData.audioBlob instanceof Blob) {
          try {
            audioBase64 = await blobToBase64(audioData.audioBlob);
          } catch (error) {
            console.error("Error convirtiendo audioBlob a base64:", error);
          }
        }

        if (audioBase64) {
          processedMessage = {
            type: 'audio_message',
            text: audioData.text || '',
            audioBase64: audioBase64,
            audioS3Url: audioS3Url,
            duration: audioData.duration || 0,
            timestamp: new Date().toISOString(),
            voicePreference: selectedVoice,
            source: message.source || 'web',
            captureType: message.captureType || 'recorder',
            metadata: {
              source: message.source || 'web',
              captureType: message.captureType || 'recorder',
              deviceInfo: 'web',
              timestamp: new Date().toISOString(),
              audioS3Url: audioS3Url
            }
          };
        } else {
          console.warn("No se pudo extraer audioBase64 válido, enviando solo texto");
          processedMessage = {
            text: audioData.text || '[Transcripción de audio no disponible]',
            voicePreference: selectedVoice,
            source: message.source || 'web',
            captureType: message.captureType || 'recorder',
            audioS3Url: audioS3Url,
            metadata: {
              source: message.source || 'web',
              captureType: message.captureType || 'recorder',
              deviceInfo: 'web',
              timestamp: new Date().toISOString(),
              audioError: true,
              audioS3Url: audioS3Url
            }
          };
        }
      } else if (message.text) {
        processedMessage = {
          text: message.text,
          voicePreference: selectedVoice,
          source: message.source,
          captureType: message.captureType,
          audioS3Url: message.audioS3Url,
          // IMPORTANTE: Preservar campos de edición si existen
          isEditedMessage: message.isEditedMessage,
          editedMessageId: message.editedMessageId,
          metadata: {
            source: message.source,
            captureType: message.captureType,
            deviceInfo: 'web',
            timestamp: new Date().toISOString(),
            audioS3Url: message.audioS3Url,
            // También en metadata
            isEditedMessage: message.isEditedMessage,
            editedMessageId: message.editedMessageId
          }
        };
      }
    }
    else if (typeof message === 'string') {
      processedMessage = {
        text: processedMessage,
        voicePreference: selectedVoice,
        source: message.source,
        captureType: message.captureType,
        metadata: {
          source: message.source,
          captureType: message.captureType,
          deviceInfo: 'web',
          timestamp: new Date().toISOString()
        }
      };
    }

    const payload = {
      message: processedMessage,
      userId,
      uid: userId,
      chatId,
      context,
      model: selectedModel,
      personality,
      userName,
      userEmail,
      voicePreference: selectedVoice,
      source: message.source,
      projectId: message.projectId
    };


    // Usar fetch para enviar y recibir SSE
    const response = await fetch(`${AGT_CHAT_STREAM_URL}/api/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      signal: abortSignal
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let streamChatId = chatId;
    let streamTitle = null;
    let streamContext = null;
    let streamId = null; // Para guardar el ID del stream

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Procesar todas las líneas completas
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            // 🔍 LOG DETALLADO: Ver TODOS los eventos SSE que llegan
            console.log('📨 [SSE] Evento recibido:', data.type, data);

            // 🚨 VALIDACIÓN CRÍTICA: Detectar eventos de tipo 'token' con data.text no-string
            if (data.type === 'token' && typeof data.text !== 'string') {
              console.error('🚨 [SSE] ¡EVENTO TOKEN INVÁLIDO DETECTADO!', {
                type: data.type,
                textType: typeof data.text,
                textValue: data.text,
                fullEvent: JSON.stringify(data, null, 2)
              });
            }

            switch (data.type) {
              case 'stream_id':
                // Guardar el ID del stream para poder cancelarlo
                streamId = data.streamId;
                if (onToken) {
                  // Enviar el streamId como primer evento
                  onToken({ type: 'stream_id', streamId });
                }
                break;

              case 'start':
                streamChatId = data.chatId;
                break;

              case 'title':
                streamTitle = data.title;
                break;

              case 'token':
                // IMPORTANTE: Solo procesar si data.text es un string
                // Si es un objeto, loguearlo y saltarlo (probablemente un error del backend)
                if (typeof data.text === 'string') {
                  fullText += data.text;
                  if (onToken) {
                    onToken(data.text);
                  }
                } else {
                  console.error('⚠️ [SSE] Token inválido recibido (no es string):', {
                    type: typeof data.text,
                    value: data.text,
                    fullData: data
                  });
                }
                break;

              case 'done':
                streamChatId = data.chatId;
                streamTitle = data.title;
                streamContext = data.updatedContext;
                if (onDone) {
                  onDone({
                    reply: data.fullText,
                    chatId: data.chatId,
                    title: data.title,
                    updatedContext: data.updatedContext
                  });
                }
                break;

              case 'error':
                if (onError) {
                  onError(new Error(data.error));
                }
                break;

              case 'preferences_updated':
                // Claude actualizó las preferencias del usuario
                if (onPreferencesUpdated && data.preferences) {
                  console.log('🔔 Preferencias actualizadas desde el asistente:', data.preferences);
                  onPreferencesUpdated(data.preferences);
                }
                break;

              case 'tool_use':
                // Claude está usando una tool
                console.log(`🔧 Tool use: ${data.toolName}`);
                if (onToken) {
                  onToken({ type: 'tool_use', toolName: data.toolName, toolId: data.toolId });
                }
                break;

              case 'executing_tools':
                // Claude está ejecutando tools
                console.log(`⚙️ Ejecutando ${data.count} tool(s)...`);
                if (onToken) {
                  onToken({ type: 'executing_tools', count: data.count });
                }
                break;

              case 'tool_marker':
                // Marcador de posición para la tool
                console.log(`📍 Tool marker recibido: ${data.marker} para ${data.toolName}`);
                if (onToken) {
                  onToken({ type: 'tool_marker', toolName: data.toolName, toolId: data.toolId, marker: data.marker });
                }
                break;

              case 'tool_start':
                // Una tool específica está comenzando a ejecutarse
                console.log(`🔧 Tool start: ${data.toolName}`);
                if (onToken) {
                  onToken({ type: 'tool_start', toolName: data.toolName, toolId: data.toolId, input: data.input });
                }
                break;

              case 'tool_executing':
                // Una tool específica está ejecutándose
                console.log(`⚙️ Tool executing: ${data.toolName}`);
                if (onToken) {
                  onToken({ type: 'tool_executing', toolName: data.toolName, toolId: data.toolId });
                }
                break;

              case 'tool_progress':
                // Progreso de una tool
                console.log(`📍 Tool progress: ${data.toolName} - ${data.phase}`);
                if (onToken) {
                  onToken({
                    type: 'tool_progress',
                    toolName: data.toolName,
                    toolId: data.toolId,
                    phase: data.phase,
                    elapsed: data.elapsed
                  });
                }
                break;

              case 'tool_complete':
                // Una tool específica terminó su ejecución
                console.log(`✓ Tool complete: ${data.toolName}`);
                if (onToken) {
                  onToken({
                    type: 'tool_complete',
                    toolName: data.toolName,
                    toolId: data.toolId,
                    success: data.success,
                    executionTime: data.executionTime,
                    input: data.input,
                    phases: data.phases
                  });
                }
                break;

              case 'tool_error':
                // Una tool falló
                console.error(`✗ Tool error: ${data.toolName} - ${data.error}`);
                if (onToken) {
                  onToken({ type: 'tool_error', toolName: data.toolName, toolId: data.toolId, error: data.error });
                }
                break;

              case 'continuing_stream':
                // Claude continúa el stream después de ejecutar tools
                console.log('🔄 Continuando stream con resultados de tools...');
                break;
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }

    return {
      reply: fullText,
      chatId: streamChatId,
      title: streamTitle,
      updatedContext: streamContext,
      selectedVoice
    };

  } catch (error) {
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

/**
 * Cancela un stream activo en el backend
 */
export const cancelAGTStream = async (streamId) => {
  try {
    const response = await fetch(`${AGT_CHAT_API_URL}/api/chat/stream/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ streamId }),
      credentials: 'include'
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return true;
  } catch (error) {
    console.error('[AGTChatService] ❌ Error en cancelAGTStream:', error);
    return false;
  }
};

/* =========================================================================
   EDICIÓN Y VERSIONADO DE MENSAJES
   ========================================================================= */

/**
 * Edita un mensaje de usuario y crea una nueva versión
 */
export const editMessage = async (userId, chatId, messageId, newText) => {
  try {
    const response = await fetch(`${AGT_CHAT_API_URL}/api/edit-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId, chatId, messageId, newText }),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[AGTChatService] ❌ Error en editMessage:', error);
    throw error;
  }
};

/**
 * Cambia a una versión diferente de un mensaje
 */
export const switchVersion = async (userId, chatId, baseMessageId, versionIndex) => {
  try {
    const response = await fetch(`${AGT_CHAT_API_URL}/api/switch-version`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId, chatId, baseMessageId, versionIndex }),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[AGTChatService] ❌ Error en switchVersion:', error);
    throw error;
  }
};

/**
 * Obtiene todas las versiones de un mensaje
 */
export const getMessageVersions = async (chatId, baseMessageId, uid) => {
  try {
    const response = await fetch(
      `${AGT_CHAT_API_URL}/api/message-versions/${chatId}/${baseMessageId}?uid=${uid}`,
      {
        method: 'GET',
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.versions;
  } catch (error) {
    console.error('[AGTChatService] ❌ Error en getMessageVersions:', error);
    throw error;
  }
};

/**
 * Envía un mensaje al backend de AGT, que a su vez invoca Anthropics/OpenAI
 */
export const sendMessageToAGT = async (
  message,
  userId,
  chatId,
  context,
  selectedModel,
  personality = 'normal',
  userName = '',
  userEmail = '',
  selectedVoice = 'alloy'
) => {
  try {
    let processedMessage = message;

    // Caso: Mensaje es un objeto con archivos (images / PDFs / etc.)
    if (typeof message === 'object' && message.files && message.files.length > 0) {
      try {
        // Procesar todos los archivos con el nuevo servicio
        const result = await ChatFilesService.processChatFiles(userId, message.files);

        const processedFiles = result.files.map((processedFile, index) => {
          const originalFile = message.files[index];
          return {
            s3Key: processedFile.s3Key,
            s3Url: processedFile.s3Url,
            bucket: processedFile.bucket,
            originalName: processedFile.fileName,
            pdfText: processedFile.extractedText || '',
            type: originalFile.type,
            data: originalFile.data,
            name: originalFile.name,
            source: originalFile.source || message.source,
            captureType: message.captureType
          };
        });

        // Si hubo errores, agregar archivos que fallaron sin procesamiento
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach((error, errorIndex) => {
            const failedFileIndex = result.processed + errorIndex;
            const originalFile = message.files[failedFileIndex];
            processedFiles.push({
              type: originalFile.type,
              data: originalFile.data,
              name: originalFile.name,
              source: originalFile.source || message.source,
              captureType: message.captureType,
              processingError: error.error
            });
          });
        }

        processedMessage = {
          text: message.text || '',
          files: processedFiles.map(file => ({
            type: file.type,
            data: file.data,
            name: file.name,
            source: file.source || message.source,
            captureType: message.captureType
          })),
          storage: processedFiles.map(file => ({
            s3Key: file.s3Key,
            s3Url: file.s3Url,
            bucket: file.bucket,
            originalName: file.originalName,
            type: file.type
          })),
          pdfs: processedFiles
            .filter(f =>
              f.type === 'application/pdf' ||
              f.type === 'application/msword' ||
              f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
              f.type === 'application/vnd.ms-excel' ||
              f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
              f.type === 'text/csv' ||
              f.type === 'text/plain'
            )
            .map(f => ({
              pdfText: f.pdfText,
              pdfUrl: f.pdfUrl,
              name: f.name,
              originalName: f.originalName
            })),
          voicePreference: selectedVoice,
          source: message.source,
          captureType: message.captureType,
          metadata: {
            source: message.source,
            captureType: message.captureType,
            deviceInfo: 'web',
            timestamp: new Date().toISOString()
          }
        };
      } catch (fileProcessingError) {
        console.error('[AGTChat] Error processing files:', fileProcessingError);
        // Si falla el procesamiento, enviar archivos sin procesar
        processedMessage = {
          text: message.text || '',
          files: message.files,
          voicePreference: selectedVoice,
          source: message.source,
          captureType: message.captureType,
          metadata: {
            source: message.source,
            captureType: message.captureType,
            deviceInfo: 'web',
            timestamp: new Date().toISOString()
          }
        };
      }
    }
    // Caso: Mensaje es un objeto con type: 'audio_message' o audioBase64
    else if (typeof message === 'object') {
      if (message.type === 'audio_message' ||
          (message.text && message.text.type === 'audio_message') ||
          message.audioBase64 ||
          (message.text && message.text.audioBase64)) {

        const audioData = message.type === 'audio_message'
          ? message
          : message.text && message.text.type === 'audio_message'
            ? message.text
            : message;

        let audioS3Url = message.audioS3Url ||
                        (message.text && message.text.audioS3Url) ||
                        audioData.audioS3Url;

        let audioBase64 = audioData.audioBase64;

        if (!audioBase64 && audioData.audioBlob instanceof Blob) {
          try {
            audioBase64 = await blobToBase64(audioData.audioBlob);
          } catch (error) {
            console.error("Error convirtiendo audioBlob a base64:", error);
          }
        }

        if (audioBase64) {
          processedMessage = {
            type: 'audio_message',
            text: audioData.text || '',
            audioBase64: audioBase64,
            audioS3Url: audioS3Url,
            duration: audioData.duration || 0,
            timestamp: new Date().toISOString(),
            voicePreference: selectedVoice,
            source: message.source || 'web',
            captureType: message.captureType || 'recorder',
            metadata: {
              source: message.source || 'web',
              captureType: message.captureType || 'recorder',
              deviceInfo: 'web',
              timestamp: new Date().toISOString(),
              audioS3Url: audioS3Url
            }
          };
        } else {
          console.warn("No se pudo extraer audioBase64 válido, enviando solo texto");
          processedMessage = {
            text: audioData.text || '[Transcripción de audio no disponible]',
            voicePreference: selectedVoice,
            source: message.source || 'web',
            captureType: message.captureType || 'recorder',
            audioS3Url: audioS3Url,
            metadata: {
              source: message.source || 'web',
              captureType: message.captureType || 'recorder',
              deviceInfo: 'web',
              timestamp: new Date().toISOString(),
              audioError: true,
              audioS3Url: audioS3Url
            }
          };
        }
      } else if (message.text) {
        processedMessage = {
          text: message.text,
          voicePreference: selectedVoice,
          source: message.source,
          captureType: message.captureType,
          audioS3Url: message.audioS3Url,
          // IMPORTANTE: Preservar campos de edición si existen
          isEditedMessage: message.isEditedMessage,
          editedMessageId: message.editedMessageId,
          metadata: {
            source: message.source,
            captureType: message.captureType,
            deviceInfo: 'web',
            timestamp: new Date().toISOString(),
            audioS3Url: message.audioS3Url,
            // También en metadata
            isEditedMessage: message.isEditedMessage,
            editedMessageId: message.editedMessageId
          }
        };
      }
    }
    // Caso: Mensaje es un string a secas
    else if (typeof message === 'string') {
      processedMessage = {
        text: processedMessage,
        voicePreference: selectedVoice,
        source: message.source,
        captureType: message.captureType,
        metadata: {
          source: message.source,
          captureType: message.captureType,
          deviceInfo: 'web',
          timestamp: new Date().toISOString()
        }
      };
    }

    const payload = {
      message: processedMessage,
      userId,
      uid: userId,
      chatId,
      context,
      model: selectedModel,
      personality,
      userName,
      userEmail,
      voicePreference: selectedVoice,
      source: message.source,
      projectId: message.projectId
    };

    const response = await apiChat.post('/api/chat', payload);

    const responseAudioUrl = response.data.audioUrl || response.data.signedUrl;

    return {
      reply: response.data.reply,
      audioBlob: response.data.audioBlob,
      audioUrl: responseAudioUrl,
      audioS3Url: responseAudioUrl,
      updatedContext: response.data.updatedContext,
      ultraThoughts: response.data.ultraThoughts || [],
      title: response.data.title,
      chatId: response.data.chatId || (response.data.chat && response.data.chat.id),
      selectedVoice
    };
  } catch (error) {
    if (error.is429) {
      return {
        isRateLimit: true,
        message: error.response.data?.message || 'Has alcanzado el límite de mensajes por hora',
        resetIn: error.response.data?.resetIn || 3600
      };
    }
    throw error;
  }
};
/* =========================================================================
   EXPORT POR DEFECTO
   ========================================================================= */
const AGTChatService = {
  supportsSSE,
  getChats,
  getChatMessages,
  createNewChat,
  updateChatCategory,
  getChatContext,
  updateChatTimestamp,
  deleteChat,
  updateChatTitle,
  sendMessageToAGT,
  sendMessageToAGTStream
};

export default AGTChatService;
